import { default as ReactEcharts } from "echarts-for-react";
import { t } from "i18next";
import { DateTime } from "luxon";
import ReactDomServer from "react-dom/server";

import _ from "lodash";
import { Equipment, Event, FShift, UsageDataset } from "../../../../../types/types";
import { hslColor, rgbColor } from "../../../../../enums/colors";
import { DataRow, PersonUsageRow, PersonUsageRowInteraction, SummaryPersonUsageRow, SummaryPersonUsageRowInteraction } from "../../../../../stores/TabsStore";
import { SetStateAction, useEffect, useState } from "react";


interface GrpahData {
  name: string;
  type: string;
  data: number[];
  color: string;
  stack?: string;
  markLine?: any;
}

interface ChartData {
  totalWorked: number;
  totalWorkedPercent: number;
  totalStopped: number;
  totalTurnedOff: number;
  totalBroken: number;
  totalRepair: number;
  totalMaintenance: number;
  totalOther: number;
  chartData: {
    days: string[],
    worked: number[],
    stopped: number[],
    broken: number[],
    repair: number[],
    maintenance: number[],
    turned_off: number[],
    other: number[]
    workedPercents: number[],
    stoppedPercents: number[],
    repairPercents: number[],
    brokenPercents: number[],
    maintenancePercents: number[], 
    otherPercents: number[],
    turned_offPercents: number[]
  }
}



export const LoadBarChart = ({data}:{data: DataRow[]}) => {
  let days = _.orderBy(_.uniq(data.map(d => d.dateShift.start_day.toSQLDate())))
  const [loading, setLoading] = useState(true)
  const [graphData, setGraphData] = useState<GrpahData[]>([])

  useEffect(() => {
    let chartData: { name: string; type: string; stack?: string; data: number[]; color: string; }[] = []

    chartData.push({
      name: `worked`,
      type: "bar",
      data: getDataForDays(data, "worked"),
      color: rgbColor.worked,
    })

    chartData.push({
      name: `stopped`,
      type: "bar",
      data: getDataForDays(data, "stopped"),
      color: rgbColor.stopped,
    })
    chartData.push({
      name: `turned_off`,
      type: "bar",
      data: getDataForDays(data, "turned_off"),
      color: rgbColor.turned_off,
    })
    chartData.push({
      name: `maintenance_worked`,
      type: "bar",
      data: getDataForDays(data, "maintenance_worked"),
      color: rgbColor.maintenanceWorked,
    })
    chartData.push({
      name: `maintenance_stopped`,
      type: "bar",
      data: getDataForDays(data, "maintenance_stopped"),
      color: rgbColor.maintenanceStopped,
    })
    chartData.push({
      name: `repair`,
      type: "bar",
      data: getDataForDays(data, "repair"),
      color: rgbColor.repair,
    })
    chartData.push({
      name: `service`,
      type: "bar",
      data: getDataForDays(data, "service"),
      color: rgbColor.service,
    })

    setGraphData(chartData)

    setLoading(false)
  }, [])

  function getDataForDays(data: DataRow[], state: "worked" | "stopped" | "turned_off" | "maintenance_worked" | "maintenance_stopped" | "repair" | "service") {
    let chartData: number[] = []
    days.map((day) => {
      let dayD = data.filter(d => d.dateShift.start_day.toSQLDate() === day)
      if(dayD.length > 0) {
        if(state === "worked") {chartData.push( (_.sum(dayD.map(e => (e.works.worked.seconds))) / (_.sum(dayD.map(e => (e.availability.total.seconds))) + _.sum(dayD.map(e => (e.unavailability.total.seconds))))) * 100 )}
        if(state === "stopped") {chartData.push( (_.sum(dayD.map(e => (e.works.stopped.seconds))) / (_.sum(dayD.map(e => (e.availability.total.seconds))) + _.sum(dayD.map(e => (e.unavailability.total.seconds))))) * 100 )}
        if(state === "turned_off") {chartData.push( ((_.sum(dayD.map(e => (e.availability.off.seconds))) + _.sum(dayD.map(e => (e.unavailability.off.seconds)))) / (_.sum(dayD.map(e => (e.availability.total.seconds))) + _.sum(dayD.map(e => (e.unavailability.total.seconds))))) * 100 )}
        if(state === "maintenance_worked") {chartData.push( (_.sum(dayD.map(e => (e.maintenance.worked.seconds))) / (_.sum(dayD.map(e => (e.availability.total.seconds))) + _.sum(dayD.map(e => (e.unavailability.total.seconds))))) * 100 )}
        if(state === "maintenance_stopped") {chartData.push( (_.sum(dayD.map(e => (e.maintenance.stopped.seconds))) / (_.sum(dayD.map(e => (e.availability.total.seconds))) + _.sum(dayD.map(e => (e.unavailability.total.seconds))))) * 100 )}
        if(state === "repair") {chartData.push( (_.sum(dayD.map(e => (e.repair.on.seconds))) / (_.sum(dayD.map(e => (e.availability.total.seconds))) + _.sum(dayD.map(e => (e.unavailability.total.seconds))))) * 100 )}
        if(state === "service") {chartData.push( (_.sum(dayD.map(e => (e.service.on.seconds))) / (_.sum(dayD.map(e => (e.availability.total.seconds))) + _.sum(dayD.map(e => (e.unavailability.total.seconds))))) * 100 )}
      } else {
        chartData.push(0)
      }
    })
    return chartData
  }


  const getOptions = () => ({
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: function (params: {name: any; seriesName: string; value: (number | string | undefined)[] }[]) {
        return ReactDomServer.renderToStaticMarkup(<Tooltip params={params} />);
      }
    },
    legend: {
      icon: "circle",
      bottom: 0,
      formatter: function (name: string) {
        return t(name);
      }
    },
    grid: {
      left: "6px",
      right: "0%",
      bottom: "30px",
      top: "10px",
      containLabel: true,
    },
    xAxis: [
      {
        type: "category",
        data: days,
        axisLabel: {
          formatter: function (value: string) {
            return DateTime.fromSQL(value).toFormat("dd");
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        splitLine: {
          lineStyle: {
            color: "rgba(128,128,128,0.1)",
          },
        },
        position: "left",
        axisLabel: {
          max: 100,
          formatter: function (value: string) {
            return `${value} %`;
          },
        },
      },
    ],
    series: graphData,
  });

  return (
    
    <ReactEcharts 
      showLoading={loading} 
      loadingOption={{
        text: '',
        color: hslColor.primary,
        // textColor: store.theme === "dark" ? hslColor.lightBorder : hslColor.darkBorder,
        // maskColor: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
        zlevel: 0,
        // Font size. Available since `v4.8.0`.
        fontSize: 12,
        // Show an animated "spinner" or not. Available since `v4.8.0`.
        showSpinner: true,
        // Radius of the "spinner". Available since `v4.8.0`.
        spinnerRadius: 8,
        // Line width of the "spinner". Available since `v4.8.0`.
        lineWidth: 2,
        // Font thick weight. Available since `v5.0.1`.
        fontWeight: 'normal',
        // Font style. Available since `v5.0.1`.
        fontStyle: 'normal',
        // Font family. Available since `v5.0.1`.
        fontFamily: 'sans-serif'
      }}
      option={getOptions()} 
      style={{ height: 200, width: "100%" }} 
      opts={{ renderer: "canvas" }} 
    />
  )
}

const Tooltip = ({ params }: { params: { name: any; seriesName: string; value: (number | string | undefined)[] }[] }) => {
  let date = DateTime.fromSQL(params[0].name!)
  return (
    <div className="relative bg-background text-xs">
      <div className="font-bold uppercase pb-3">{date.weekdayShort} {date.toFormat("dd.MM.yyyy")}</div>
      <div className="flex flex-col gap-1">
        {params.map(item => (
          <div key={item.name} className="flex w-48 justify-between">
            <div className="flex truncate">{t(`${item.seriesName}`)}</div>
            <div className="font-bold">{Number(item.value).toFixed(2)}%</div>
          </div>
        ))}
      </div>

    </div>
  );
};